import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { useSelector } from 'react-redux'
import { type AppState } from 'state/index'
import { unitConvert } from '@farmtrx/units'
import { useTranslation } from 'react-i18next'

const legendStyles = {
  overflowY: 'auto',
  maxHeight: '210px',
}

export default function EqualRangeLegend(props: {
  colors: string[]
  quantities: number[]
  layer: { name: string; translation: string }
  numRanges: number
  cropBuPerTonne: number
  selectedSwathParameter?: string
  showUnits?: boolean
}) {
  const {
    colors,
    quantities,
    layer,
    numRanges,
    cropBuPerTonne,
    selectedSwathParameter,
    showUnits,
  } = props
  const preferenceUnits = useSelector(
    (state: AppState) => state.account.singleton.prefs?.units,
  )

  const { t } = useTranslation()

  const getUnits = () => {
    if (layer.name === 'dem') {
      return t(preferenceUnits?.['length_in'])
    } else if (layer.translation === 'swath_map') {
      if (selectedSwathParameter === 'moisture') {
        return '%'
      } else if (selectedSwathParameter === 'speed') {
        return t(preferenceUnits?.[`${selectedSwathParameter}_in`])
      } else if (selectedSwathParameter === 'header_height') {
        return t(preferenceUnits?.['length_in'])
      }
    }
    return t(preferenceUnits?.['yieldunit_in'])
  }

  const getMinMaxConversions = (quantity) => {
    let value
    if (layer.name === 'dem') {
      const to = preferenceUnits?.['length_in']
      const from = unitConvert.METERS
      return parseFloat(
        unitConvert.convertLength(quantity, to, from).toFixed(2),
      )
    } else if (
      layer.name === 'corrected_yield' ||
      layer.name === 'yield' || // this is required for the crop legend page
      (layer.name === 'swath' && selectedSwathParameter === 'yield')
    ) {
      value = parseFloat(
        unitConvert
          .convertYieldToPref(
            quantity,
            preferenceUnits?.['yieldunit_in'],
            cropBuPerTonne,
          )
          .toFixed(2),
      )
    } else {
      let to, from, p
      if (selectedSwathParameter === 'speed') {
        p = 'speed_in'
        to = preferenceUnits?.[p]
        from = 'kmh'

        return parseFloat(
          unitConvert.convertSpeed(quantity, to, from).toFixed(2),
        )
      } else if (selectedSwathParameter === 'header_height') {
        p = 'length_in'
        to = preferenceUnits?.[p]
        from = unitConvert.MM

        return parseFloat(
          unitConvert.convertLength(quantity, to, from).toFixed(2),
        )
      }
      if (selectedSwathParameter === 'moisture') {
        return quantity // Moisture is already in percentage
      }
    }

    return value
  }

  return (
    <Box>
      {showUnits ? (
        <Typography variant="body2">Units: {getUnits()}</Typography>
      ) : null}
      <TableContainer component={Paper} sx={showUnits ? null : legendStyles}>
        <Table aria-label="min-max-color-ranges" size="small">
          <TableHead
            sx={{
              '& .MuiTableCell-root': {
                padding: '0px',
              },
            }}
          >
            <TableRow>
              <TableCell sx={{ width: '30%' }}></TableCell>
              <TableCell sx={{ width: '30%', textAlign: 'center' }}>
                {t('min')}
              </TableCell>
              <TableCell sx={{ width: '30%', textAlign: 'center' }}>
                {t('max')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Array.from({ length: numRanges }).map((_, index) => (
              <TableRow
                key={index}
                sx={{
                  '& .MuiTableCell-root': {
                    padding: '0px',
                  },
                  '&:last-child td, &:last-child th': { border: 0 },
                }}
              >
                <TableCell component="th" scope="row" align="center">
                  <svg
                    height="25"
                    width="45"
                    key={index}
                    style={{ verticalAlign: 'middle' }}
                  >
                    <rect
                      width="45"
                      height="25"
                      strokeWidth="1"
                      stroke="rgb(0, 0, 0)"
                      fill={colors[index]}
                    />
                  </svg>
                </TableCell>
                <TableCell component="th" scope="row" align="center">
                  {index === 0
                    ? '-'
                    : index === numRanges - 1
                      ? `> ${getMinMaxConversions(quantities[index])}`
                      : getMinMaxConversions(quantities[index])}
                </TableCell>

                <TableCell component="th" scope="row" align="center">
                  {index === 0
                    ? `< ${getMinMaxConversions(quantities[1])}`
                    : index === numRanges - 1
                      ? '-'
                      : getMinMaxConversions(quantities[index + 1])}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}
